import { useEffect, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { selectAppPermissions } from "../../../store/auth/selectors";
import {
  Modal,
  Breadcrumb,
  Statistic,
  Button,
  Table,
  Spin,
  Empty,
  Input,
  Select,
  Row,
  Col,
  Typography,
  Space,
  Dropdown,
  Popconfirm,
  Progress,
  Tooltip,
  message
} from "antd";
import {
  DownloadOutlined,
  FolderFilled,
  FilePdfOutlined,
  FileExcelOutlined,
  FileWordOutlined,
  FileImageOutlined,
  FileZipOutlined,
  FileOutlined,
  EyeOutlined,
  HomeOutlined,
  CloudSyncOutlined,
  UploadOutlined,
  CloudUploadOutlined,
  FolderAddOutlined,
  MoreOutlined,
  EditOutlined,
  SwapOutlined,
  DeleteOutlined,
  HistoryOutlined,
  GoogleOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  SyncOutlined,
  ArrowLeftOutlined,
  SearchOutlined
} from "@ant-design/icons";
import { saveAs } from "file-saver";
import {
  getDriveFolder,
  descargarInformeTecnicoFinal,
  obtenerEstadoZipCache,
  generarZipCache as generarZipCacheApi,
  cancelarZipCache as cancelarZipCacheApi,
  setDriveFolderConfig,
  subirArchivos,
  actualizarArchivoContenido,
  renombrarItem as renombrarItemApi,
  crearCarpeta as crearCarpetaApi,
  eliminarItem as eliminarItemApi
} from "../../../services/informeTecnicoFinal/informeTecnicoFinalService";
import HistorialPanel from "./HistorialPanel";

const { Text, Title } = Typography;

const NAVY = "#042956";

/* ---------- Helpers de presentación ---------- */

function formatSize(bytes) {
  if (bytes == null || bytes === "") return "—";
  const n = Number(bytes);
  if (Number.isNaN(n) || n === 0) return "—";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

// Los Docs/Sheets/Slides nativos de Google no tienen contenido binario propio,
// por eso Drive no reporta `size` para ellos (no es un dato faltante).
function isGoogleNativeFile(mimeType) {
  return (mimeType || "").startsWith("application/vnd.google-apps.");
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  } catch {
    return "—";
  }
}

// Categoriza un item para el ícono y para el filtro de tipo de la tabla.
function detectarTipo(item) {
  if (item.isFolder) return "carpeta";
  const mime = (item.mimeType || "").toLowerCase();
  const name = (item.name || "").toLowerCase();
  const is = (t) => mime.includes(t) || name.endsWith(t);
  if (is("pdf")) return "pdf";
  if (mime.includes("spreadsheet") || is(".xlsx") || is(".xls") || is(".csv")) return "excel";
  if (mime.includes("word") || mime.includes("document") || is(".docx") || is(".doc")) return "word";
  if (mime.includes("image") || is(".png") || is(".jpg") || is(".jpeg") || is(".gif")) return "imagen";
  if (mime.includes("zip") || is(".zip") || is(".rar")) return "comprimido";
  if (mime.includes("video") || is(".mp4") || is(".avi") || is(".mov") || is(".mkv")) return "video";
  return "otro";
}

const TIPO_OPTIONS = [
  { value: "todos", label: "Todos los tipos" },
  { value: "pdf", label: "PDF" },
  { value: "word", label: "Word" },
  { value: "excel", label: "Excel" },
  { value: "imagen", label: "Imagen" },
  { value: "comprimido", label: "Comprimido" },
  { value: "video", label: "Video" },
  { value: "carpeta", label: "Carpeta" },
  { value: "otro", label: "Otro" }
];

const ICONOS_TIPO = {
  carpeta: <FolderFilled style={{ color: "#f5b301", fontSize: 20 }} />,
  pdf: <FilePdfOutlined style={{ color: "#e53935", fontSize: 20 }} />,
  excel: <FileExcelOutlined style={{ color: "#2e7d32", fontSize: 20 }} />,
  word: <FileWordOutlined style={{ color: "#1565c0", fontSize: 20 }} />,
  imagen: <FileImageOutlined style={{ color: "#8e24aa", fontSize: 20 }} />,
  comprimido: <FileZipOutlined style={{ color: "#6d4c41", fontSize: 20 }} />
};

function fileIcon(item) {
  return ICONOS_TIPO[detectarTipo(item)] || <FileOutlined style={{ color: "#8c9bab", fontSize: 20 }} />;
}

function elapsedLabel(iniciadoEn) {
  if (!iniciadoEn) return null;
  const seconds = Math.max(0, Math.round((Date.now() - new Date(iniciadoEn).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)} min`;
}

const extraerFolderId = (value) => {
  const match = value.match(/[-\w]{20,}/);
  return match ? match[0] : value.trim();
};

// Subir árboles de carpetas de varios GB en una sola petición es frágil
// (timeouts, límites del servidor). Se divide en lotes por tamaño y cantidad.
const UPLOAD_BATCH_SIZE_MB = 500;
const UPLOAD_BATCH_MAX_FILES = 20;

function buildUploadBatches(files) {
  const batches = [];
  let current = [];
  let currentSizeMB = 0;

  for (const file of files) {
    const fileSizeMB = file.size / (1024 * 1024);
    const wouldExceedSize = currentSizeMB + fileSizeMB > UPLOAD_BATCH_SIZE_MB;
    const wouldExceedCount = current.length >= UPLOAD_BATCH_MAX_FILES;

    if (current.length > 0 && (wouldExceedSize || wouldExceedCount)) {
      batches.push(current);
      current = [];
      currentSizeMB = 0;
    }

    current.push(file);
    currentSizeMB += fileSizeMB;
  }

  if (current.length > 0) batches.push(current);
  return batches;
}

export default function InformeTecnicoFinalModal({ visible, proyecto, nombreProyecto, onClose }) {
  const permissions = useSelector((state) => selectAppPermissions(state, "informe_tecnico_final"));
  const permissionInsert = permissions.priv_insert === "Y";
  const permissionUpdate = permissions.priv_update === "Y";
  const permissionDelete = permissions.priv_delete === "Y";
  const permissionExport = permissions.priv_export === "Y";

  // App aparte, mismo criterio que el widget de espacio en InformeTecnicoFinalPage:
  // no todos deben poder saltar directo a la carpeta cruda en Drive.
  const espacioPermissions = useSelector((state) =>
    selectAppPermissions(state, "informe_tecnico_final_espacio_drive")
  );
  const permissionAbrirDrive = espacioPermissions.priv_print === "Y";

  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [maximized, setMaximized] = useState(false);
  const [folderData, setFolderData] = useState(null);
  const [folderError, setFolderError] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [manualInput, setManualInput] = useState("");
  const [configurando, setConfigurando] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [rootFolderId, setRootFolderId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgressText, setUploadProgressText] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [failedBatches, setFailedBatches] = useState([]);
  const [uploadCancelled, setUploadCancelled] = useState(false);
  const [cancelandoZip, setCancelandoZip] = useState(false);
  const [nuevaCarpetaVisible, setNuevaCarpetaVisible] = useState(false);
  const [nuevaCarpetaNombre, setNuevaCarpetaNombre] = useState("");
  const [creandoCarpeta, setCreandoCarpeta] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [historialVisible, setHistorialVisible] = useState(false);
  const [zipEstado, setZipEstado] = useState(null);
  const [zipSizeBytes, setZipSizeBytes] = useState(0);
  const [zipGeneradoEn, setZipGeneradoEn] = useState(null);
  const [zipTotalArchivos, setZipTotalArchivos] = useState(0);
  const [zipArchivosProcesados, setZipArchivosProcesados] = useState(0);
  const [zipIniciadoEn, setZipIniciadoEn] = useState(null);
  const [generandoZip, setGenerandoZip] = useState(false);
  const [filtroNombre, setFiltroNombre] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("todos");

  const uploadInputRef = useRef(null);
  const folderUploadInputRef = useRef(null);
  const replaceInputRef = useRef(null);
  const replaceTargetRef = useRef(null);
  // Controlador de la petición HTTP en curso (para poder abortarla) y bandera
  // para no seguir con los lotes que faltan tras un cancelar.
  const uploadAbortRef = useRef(null);
  const uploadCancelFlagRef = useRef(false);
  // Último set de lotes armado (subir archivos o carpeta), para poder
  // reintentar solo los que fallaron sin rehacer todo desde cero.
  const lastUploadRef = useRef(null);
  const deleteAbortRef = useRef(null);

  const fetchFolder = useCallback(async (folderId) => {
    setLoading(true);
    const { data, message: errMsg } = await getDriveFolder(proyecto, folderId);
    setFolderData(data);
    setFolderError(data ? null : errMsg || "No se pudo consultar la carpeta de Drive");
    setLoading(false);
    return data;
  }, [proyecto]);

  const fetchZipEstado = useCallback(async () => {
    const { data } = await obtenerEstadoZipCache(proyecto);
    if (data) {
      setZipEstado(data.estado);
      setZipSizeBytes(data.sizeBytes || 0);
      setZipGeneradoEn(data.generatedAt || null);
      setZipTotalArchivos(data.totalArchivos || 0);
      setZipArchivosProcesados(data.archivosProcesados || 0);
      setZipIniciadoEn(data.iniciadoEn || null);
    }
  }, [proyecto]);

  const cargarInicial = useCallback(async () => {
    setBreadcrumb([]);
    const data = await fetchFolder(undefined);
    if (data?.configurado) {
      const nombreRaiz = nombreProyecto ? `${proyecto} - ${nombreProyecto}` : proyecto;
      setBreadcrumb([{ id: data.folderId, name: nombreRaiz }]);
      setCurrentFolderId(data.folderId);
      setRootFolderId(data.folderId);
      fetchZipEstado();
    }
  }, [fetchFolder, fetchZipEstado, nombreProyecto, proyecto]);

  useEffect(() => {
    if (!visible || !proyecto) return;
    setFolderData(null);
    setFolderError(null);
    setZipEstado(null);
    setZipSizeBytes(0);
    setZipGeneradoEn(null);
    setZipTotalArchivos(0);
    setZipArchivosProcesados(0);
    setZipIniciadoEn(null);
    cargarInicial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, proyecto]);

  // Mientras se genera el paquete en background, se consulta el estado cada
  // pocos segundos hasta que quede "vigente" (o falle).
  useEffect(() => {
    if (!visible || zipEstado !== "generando") return;
    const interval = setInterval(fetchZipEstado, 5000);
    return () => clearInterval(interval);
  }, [visible, zipEstado, fetchZipEstado]);

  const buildPath = (name) => [...breadcrumb.map((b) => b.name), name].join("/");

  const abrirCarpeta = async (item) => {
    await fetchFolder(item.id);
    setBreadcrumb((prev) => [...prev, { id: item.id, name: item.name }]);
    setCurrentFolderId(item.id);
    setFiltroNombre("");
    setFiltroTipo("todos");
    // Los lotes pendientes de reintentar apuntan a la carpeta donde se
    // originó la subida; al salir de ahí ya no aplican.
    setFailedBatches([]);
  };

  const irABreadcrumb = async (index) => {
    const target = breadcrumb[index];
    await fetchFolder(target.id);
    setBreadcrumb((prev) => prev.slice(0, index + 1));
    setCurrentFolderId(target.id);
    setFiltroNombre("");
    setFiltroTipo("todos");
    setFailedBatches([]);
  };

  const handleVolver = () => irABreadcrumb(breadcrumb.length - 2);

  const handleDescargar = async () => {
    setDownloading(true);
    setDownloadProgress(0);
    try {
      const blob = await descargarInformeTecnicoFinal(proyecto, (progressEvent) => {
        if (zipSizeBytes > 0) {
          const pct = Math.min(99, Math.round((progressEvent.loaded / zipSizeBytes) * 100));
          setDownloadProgress(pct);
        }
      });

      setDownloadProgress(100);
      saveAs(blob, `${proyecto}_informe_tecnico_final.zip`);
    } catch (error) {
      console.error("Error al descargar informe técnico final:", error);
      message.error("No se pudo descargar el paquete. Intenta nuevamente.");
      fetchZipEstado();
    } finally {
      setDownloading(false);
      setTimeout(() => setDownloadProgress(0), 1200);
    }
  };

  const handleGenerarZip = async () => {
    setGenerandoZip(true);
    try {
      await generarZipCacheApi(proyecto);
      message.success("Generación del paquete iniciada");
      setZipEstado("generando");
      setZipTotalArchivos(0);
      setZipArchivosProcesados(0);
      setZipIniciadoEn(new Date().toISOString());
    } catch (error) {
      if (error.response?.status === 409) {
        message.warning("Ya se está generando el paquete de descarga");
        setZipEstado("generando");
      } else {
        console.error("Error al iniciar la generación del paquete:", error);
        message.error("No se pudo iniciar la generación del paquete");
      }
    } finally {
      setGenerandoZip(false);
    }
  };

  const handleCancelarZip = async () => {
    setCancelandoZip(true);
    try {
      await cancelarZipCacheApi(proyecto);
      message.success("Generación cancelada");
      await fetchZipEstado();
    } catch (error) {
      console.error("Error al cancelar la generación del paquete:", error);
      message.error("No se pudo cancelar la generación");
    } finally {
      setCancelandoZip(false);
    }
  };

  const handleConfigurar = async () => {
    if (!manualInput.trim()) return;
    setConfigurando(true);
    try {
      await setDriveFolderConfig(proyecto, extraerFolderId(manualInput));
      message.success("Carpeta configurada correctamente");
      setManualInput("");
      await cargarInicial();
    } catch (error) {
      console.error("Error al configurar carpeta de Drive:", error);
      message.error("No se pudo configurar la carpeta");
    } finally {
      setConfigurando(false);
    }
  };

  const handleUploadClick = () => uploadInputRef.current?.click();

  // Motor común para "Subir archivos" (un solo lote) y "Subir carpeta"
  // (varios lotes). `indexes` es la lista de posiciones de `batches` a
  // procesar en esta corrida — al reintentar solo se pasan las que fallaron.
  const subirLotes = async (batches, pathsBatches, indexes) => {
    setUploading(true);
    setUploadCancelled(false);
    uploadCancelFlagRef.current = false;
    setUploadProgress(0);

    const nuevosFallidos = [];
    let procesados = 0;

    for (const i of indexes) {
      if (uploadCancelFlagRef.current) {
        nuevosFallidos.push(i);
        continue;
      }

      const batch = batches[i];
      const paths = pathsBatches[i];
      setUploadProgressText(
        indexes.length > 1
          ? `Subiendo lote ${procesados + 1} de ${indexes.length} (${batch.length} archivos)…`
          : `Subiendo ${batch.length} archivo(s)…`
      );

      const controller = new AbortController();
      uploadAbortRef.current = controller;
      // Snapshot: evita que el callback de progreso (que puede seguir
      // disparando mientras esta petición está en vuelo) capture la
      // variable `procesados` mutable del loop en vez de su valor de esta
      // iteración.
      const procesadosPrevios = procesados;

      try {
        await subirArchivos(
          proyecto,
          currentFolderId,
          batch,
          paths,
          (progressEvent) => {
            if (progressEvent.total) {
              const progresoLote = progressEvent.loaded / progressEvent.total;
              setUploadProgress(Math.round(((procesadosPrevios + progresoLote) / indexes.length) * 100));
            }
          },
          controller.signal
        );
      } catch (error) {
        if (error.code === "ERR_CANCELED") {
          nuevosFallidos.push(i);
          uploadCancelFlagRef.current = true;
        } else {
          console.error(`Error al subir el lote ${i + 1}:`, error);
          nuevosFallidos.push(i);
        }
      }

      procesados += 1;
      setUploadProgress(Math.round((procesados / indexes.length) * 100));
    }

    uploadAbortRef.current = null;
    const fueCancelado = uploadCancelFlagRef.current;
    setFailedBatches(nuevosFallidos);
    setUploadCancelled(fueCancelado && nuevosFallidos.length > 0);
    setUploading(false);
    setUploadProgressText("");
    setTimeout(() => setUploadProgress(0), 1000);

    const exitosos = indexes.length - nuevosFallidos.length;
    if (nuevosFallidos.length === 0) {
      message.success("Subida completada correctamente");
    } else if (fueCancelado) {
      message.warning(
        `Subida cancelada. ${exitosos} de ${indexes.length} lote(s) alcanzaron a subirse; el resto quedó pendiente.`
      );
    } else {
      message.warning(
        `${exitosos} de ${indexes.length} lote(s) subidos, ${nuevosFallidos.length} con error. Puedes reintentar solo los que fallaron.`
      );
    }

    await fetchFolder(currentFolderId);
    await fetchZipEstado();
  };

  const handleCancelarSubida = () => {
    uploadCancelFlagRef.current = true;
    uploadAbortRef.current?.abort();
  };

  const handleReintentarFallidos = async () => {
    const stored = lastUploadRef.current;
    if (!stored || !failedBatches.length) return;
    await subirLotes(stored.batches, stored.pathsBatches, failedBatches);
  };

  const handleDescartarPendientes = () => {
    setFailedBatches([]);
    setUploadCancelled(false);
    lastUploadRef.current = null;
  };

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length || !currentFolderId) return;

    // Archivos sueltos van directo a currentFolderId como un único lote: no
    // se les antepone el breadcrumb (eso duplicaba el árbol de carpetas).
    const batches = [files];
    const pathsBatches = [files.map((f) => f.name)];
    lastUploadRef.current = { batches, pathsBatches };
    setFailedBatches([]);

    await subirLotes(batches, pathsBatches, [0]);
  };

  const handleFolderUploadClick = () => folderUploadInputRef.current?.click();

  const handleFolderSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length || !currentFolderId) return;

    const batches = buildUploadBatches(files);
    const pathsBatches = batches.map((batch) => batch.map((f) => f.webkitRelativePath || f.name));
    lastUploadRef.current = { batches, pathsBatches };
    setFailedBatches([]);

    await subirLotes(
      batches,
      pathsBatches,
      batches.map((_, i) => i)
    );
  };

  const handleReplaceClick = (item) => {
    replaceTargetRef.current = item;
    replaceInputRef.current?.click();
  };

  const handleReplaceFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const target = replaceTargetRef.current;
    if (!file || !target) return;
    setUploading(true);
    try {
      await actualizarArchivoContenido(proyecto, target.id, file, buildPath(target.name));
      message.success("Archivo actualizado correctamente");
      await fetchFolder(currentFolderId);
      await fetchZipEstado();
    } catch (error) {
      console.error("Error al actualizar archivo:", error);
      message.error("No se pudo actualizar el archivo");
    } finally {
      setUploading(false);
      replaceTargetRef.current = null;
    }
  };

  const openRename = (item) => {
    setRenameTarget(item);
    setRenameValue(item.name);
  };

  const closeRename = () => {
    setRenameTarget(null);
    setRenameValue("");
  };

  const handleRenameConfirm = async () => {
    if (!renameTarget || !renameValue.trim()) return;
    setRenaming(true);
    try {
      await renombrarItemApi(proyecto, {
        fileId: renameTarget.id,
        nuevoNombre: renameValue.trim(),
        tipo: renameTarget.isFolder ? "carpeta" : "archivo",
        path: buildPath(renameValue.trim()),
        nombreAnterior: renameTarget.name
      });
      message.success("Renombrado correctamente");
      closeRename();
      await fetchFolder(currentFolderId);
      await fetchZipEstado();
    } catch (error) {
      console.error("Error al renombrar:", error);
      message.error("No se pudo renombrar el elemento");
    } finally {
      setRenaming(false);
    }
  };

  const handleCrearCarpeta = async () => {
    if (!nuevaCarpetaNombre.trim() || !currentFolderId) return;
    setCreandoCarpeta(true);
    try {
      await crearCarpetaApi(proyecto, currentFolderId, nuevaCarpetaNombre.trim(), breadcrumb.map((b) => b.name).join("/"));
      message.success("Carpeta creada correctamente");
      setNuevaCarpetaVisible(false);
      setNuevaCarpetaNombre("");
      await fetchFolder(currentFolderId);
      await fetchZipEstado();
    } catch (error) {
      console.error("Error al crear carpeta:", error);
      message.error("No se pudo crear la carpeta");
    } finally {
      setCreandoCarpeta(false);
    }
  };

  const handleEliminar = async (item) => {
    setDeletingId(item.id);
    const controller = new AbortController();
    deleteAbortRef.current = controller;
    try {
      await eliminarItemApi(
        proyecto,
        {
          fileId: item.id,
          tipo: item.isFolder ? "carpeta" : "archivo",
          nombre: item.name,
          path: buildPath(item.name)
        },
        controller.signal
      );
      message.success("Elemento movido a la papelera de Drive");
      await fetchFolder(currentFolderId);
      await fetchZipEstado();
    } catch (error) {
      if (error.code === "ERR_CANCELED") {
        message.info("Eliminación cancelada");
      } else {
        console.error("Error al eliminar:", error);
        message.error("No se pudo eliminar el elemento");
      }
    } finally {
      setDeletingId(null);
      deleteAbortRef.current = null;
    }
  };

  const handleCancelarEliminar = () => {
    deleteAbortRef.current?.abort();
  };

  const columns = [
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
      render: (name, item) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {fileIcon(item)}
          {item.isFolder ? (
            <a onClick={() => abrirCarpeta(item)} style={{ color: NAVY, fontWeight: 500 }}>
              {name}
            </a>
          ) : (
            <Text style={{ color: "#37475a" }}>{name}</Text>
          )}
        </div>
      )
    },
    {
      title: "Tamaño",
      dataIndex: "size",
      key: "size",
      width: 120,
      render: (size, item) => {
        // Drive no reporta un tamaño agregado para carpetas (ni siquiera su
        // propia interfaz lo muestra sin abrir "Detalles"); calcularlo aquí
        // exigiría recorrer cada carpeta recursivamente en cada render de la
        // tabla, justo lo que se quitó por lento. Se deja en blanco a
        // propósito, no es un dato faltante.
        if (item.isFolder) return null;
        const text = isGoogleNativeFile(item.mimeType) ? "Doc. de Google" : formatSize(size);
        return <Text style={{ color: "#8c9bab" }}>{text}</Text>;
      }
    },
    {
      title: "Última modificación",
      dataIndex: "modifiedTime",
      key: "modifiedTime",
      width: 170,
      render: (t) => <Text style={{ color: "#8c9bab" }}>{formatDate(t)}</Text>
    },
    {
      title: "Acción",
      key: "accion",
      width: 220,
      align: "center",
      render: (_, item) => {
        const menuItems = [
          ...(permissionUpdate ? [{ key: "renombrar", icon: <EditOutlined />, label: "Renombrar" }] : []),
          ...(permissionUpdate && !item.isFolder
            ? [{ key: "reemplazar", icon: <SwapOutlined />, label: "Reemplazar contenido" }]
            : []),
          ...(permissionDelete
            ? [
                {
                  key: "eliminar",
                  icon: <DeleteOutlined />,
                  danger: true,
                  disabled: uploading,
                  label: uploading ? (
                    <Tooltip title="Espera a que termine la subida en curso">
                      <span>Eliminar</span>
                    </Tooltip>
                  ) : (
                    <Popconfirm
                      title={`Eliminar "${item.name}"`}
                      description="Se moverá a la papelera de Drive."
                      onConfirm={() => handleEliminar(item)}
                      okText="Eliminar"
                      cancelText="Cancelar"
                    >
                      <span>Eliminar</span>
                    </Popconfirm>
                  )
                }
              ]
            : [])
        ];

        const onMenuClick = ({ key }) => {
          if (key === "renombrar") openRename(item);
          else if (key === "reemplazar") handleReplaceClick(item);
        };

        return (
          <Space size={8}>
            {item.isFolder ? (
              <Tooltip title="Entrar a esta carpeta">
                <Button
                  size="small"
                  icon={<FolderFilled />}
                  onClick={() => abrirCarpeta(item)}
                  style={{ borderRadius: 8, color: NAVY, borderColor: "#d7dee7" }}
                >
                  Abrir
                </Button>
              </Tooltip>
            ) : (
              <Tooltip title="Abre el archivo en una pestaña nueva de Google Drive">
                <Button
                  size="small"
                  icon={<EyeOutlined />}
                  href={item.webViewLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ borderRadius: 8, color: NAVY, borderColor: "#d7dee7" }}
                >
                  Ver en Drive
                </Button>
              </Tooltip>
            )}
            {deletingId === item.id && (
              <Tooltip title="Cancela la eliminación en curso">
                <Button size="small" danger onClick={handleCancelarEliminar} style={{ borderRadius: 8 }}>
                  Cancelar
                </Button>
              </Tooltip>
            )}
            {menuItems.length > 0 && (
              <Dropdown menu={{ items: menuItems, onClick: onMenuClick }} trigger={["click"]}>
                <Tooltip title="Más acciones">
                  <Button
                    size="small"
                    icon={<MoreOutlined />}
                    loading={deletingId === item.id}
                    style={{ borderRadius: 8, color: NAVY, borderColor: "#d7dee7" }}
                  />
                </Tooltip>
              </Dropdown>
            )}
          </Space>
        );
      }
    }
  ];

  const itemsFiltrados = (folderData?.items || []).filter((item) => {
    const coincideNombre = !filtroNombre || item.name.toLowerCase().includes(filtroNombre.toLowerCase());
    const coincideTipo = filtroTipo === "todos" || detectarTipo(item) === filtroTipo;
    return coincideNombre && coincideTipo;
  });

  /* ---------- Contenido según estado ---------- */
  let content;

  if (loading && !folderData) {
    content = (
      <div style={{ padding: "80px 0", textAlign: "center" }}>
        <Spin size="large" tip="Cargando carpeta de Drive…">
          <div style={{ height: 60 }} />
        </Spin>
      </div>
    );
  } else if (folderError) {
    content = (
      <div style={{ padding: "48px 24px", maxWidth: 520, margin: "0 auto", textAlign: "center" }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Text style={{ color: "#cf1322", fontSize: 14 }}>{folderError}</Text>
          }
        />
        <Button
          style={{ marginTop: 16, borderRadius: 8, color: NAVY, borderColor: "#d7dee7" }}
          onClick={cargarInicial}
        >
          Reintentar
        </Button>
      </div>
    );
  } else if (!folderData?.configurado) {
    content = (
      <div style={{ padding: "48px 24px", maxWidth: 480, margin: "0 auto", textAlign: "center" }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Text style={{ color: "#5b6b7c", fontSize: 14 }}>
              Este proyecto aún no tiene una carpeta de Drive configurada
            </Text>
          }
        />
        {permissionUpdate ? (
          <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 12 }}>
            <Input
              size="large"
              value={manualInput}
              onChange={(e) => setManualInput(e.target.value)}
              placeholder="Pega el ID o enlace de la carpeta de Drive"
              prefix={<CloudSyncOutlined style={{ color: "#8c9bab" }} />}
              style={{ borderRadius: 10 }}
            />
            <Button
              type="primary"
              size="large"
              loading={configurando}
              disabled={!manualInput.trim()}
              onClick={handleConfigurar}
              style={{ background: NAVY, borderRadius: 10, height: 42, fontWeight: 500 }}
            >
              Configurar
            </Button>
          </div>
        ) : (
          <Text style={{ display: "block", marginTop: 16, color: "#8c9bab", fontSize: 13 }}>
            No tienes permisos para configurar la carpeta de este proyecto.
          </Text>
        )}
      </div>
    );
  } else {
    content = (
      <>
        <div
          style={{
            background: "#f7f9fc",
            borderRadius: 12,
            padding: 16,
            marginBottom: 16,
            border: "1px solid #eef1f5"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            {breadcrumb.length > 1 && (
              <Button
                size="small"
                icon={<ArrowLeftOutlined />}
                onClick={handleVolver}
                style={{ borderRadius: 8, color: NAVY, borderColor: "#d7dee7" }}
              >
                Volver
              </Button>
            )}
            <Breadcrumb
              style={{ margin: 0 }}
              items={breadcrumb.map((crumb, idx) => ({
                title:
                  idx === 0 ? (
                    <a onClick={() => irABreadcrumb(0)}>
                      <HomeOutlined /> {crumb.name}
                    </a>
                  ) : idx === breadcrumb.length - 1 ? (
                    <span style={{ color: NAVY, fontWeight: 600 }}>{crumb.name}</span>
                  ) : (
                    <a onClick={() => irABreadcrumb(idx)}>{crumb.name}</a>
                  )
              }))}
            />
          </div>

          <Row gutter={[16, 16]} align="middle">
            <Col xs={8} md={5}>
              <Tooltip title="Cantidad de archivos en esta carpeta (no incluye subcarpetas)">
                <div>
                  <Statistic
                    title={<span style={{ color: "#8c9bab", fontSize: 13 }}>Archivos</span>}
                    value={folderData.totalArchivos}
                    valueStyle={{ color: NAVY, fontSize: 22, fontWeight: 700 }}
                  />
                </div>
              </Tooltip>
            </Col>
            <Col xs={8} md={5}>
              <Tooltip title="Cantidad de subcarpetas dentro de esta carpeta">
                <div>
                  <Statistic
                    title={<span style={{ color: "#8c9bab", fontSize: 13 }}>Carpetas</span>}
                    value={folderData.totalCarpetas}
                    valueStyle={{ color: NAVY, fontSize: 22, fontWeight: 700 }}
                  />
                </div>
              </Tooltip>
            </Col>
            <Col xs={24} md={6}>
              <Tooltip title="Fecha del archivo modificado más recientemente en esta carpeta (no incluye subcarpetas)">
                <div>
                  <div style={{ color: "#8c9bab", fontSize: 13, marginBottom: 4 }}>Última modificación</div>
                  <div style={{ color: NAVY, fontSize: 15, fontWeight: 600 }}>
                    {formatDate(folderData.ultimaModificacion)}
                  </div>
                </div>
              </Tooltip>
            </Col>
            <Col xs={24} md={8}>
              {!permissionExport && (
                <div style={{ textAlign: "center", fontSize: 12, color: "#8c9bab" }}>
                  No tienes permisos para descargar el paquete
                </div>
              )}
              {permissionExport && zipEstado === "vigente" && (
                <>
                  <Tooltip title="Descarga el paquete ZIP ya generado con todo el contenido del proyecto">
                    <Button
                      type="primary"
                      block
                      icon={<DownloadOutlined />}
                      loading={downloading}
                      onClick={handleDescargar}
                      style={{
                        background: NAVY,
                        borderRadius: 10,
                        height: 44,
                        fontWeight: 500,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                        whiteSpace: "normal"
                      }}
                    >
                      {downloading ? `Descargando… ${downloadProgress}%` : "Descargar todo"}
                    </Button>
                  </Tooltip>
                  {downloading && (
                    <Progress
                      percent={downloadProgress}
                      size="small"
                      showInfo={false}
                      strokeColor={NAVY}
                      style={{ marginTop: 6 }}
                    />
                  )}
                  {!downloading && zipGeneradoEn && (
                    <div style={{ marginTop: 6, fontSize: 11, color: "#8c9bab", textAlign: "center" }}>
                      Paquete generado: {formatDate(zipGeneradoEn)}
                    </div>
                  )}
                </>
              )}
              {permissionExport && zipEstado === "generando" && (
                <div style={{ textAlign: "center" }}>
                  <Button
                    block
                    disabled
                    icon={<SyncOutlined spin />}
                    style={{
                      borderRadius: 10,
                      height: 44,
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      whiteSpace: "normal"
                    }}
                  >
                    Generando…
                  </Button>
                  {zipTotalArchivos > 0 && (
                    <Progress
                      percent={Math.round((zipArchivosProcesados / zipTotalArchivos) * 100)}
                      size="small"
                      strokeColor={NAVY}
                      style={{ marginTop: 6 }}
                    />
                  )}
                  <div style={{ marginTop: 2, fontSize: 11, color: "#8c9bab" }}>
                    {zipTotalArchivos > 0
                      ? `${zipArchivosProcesados} de ${zipTotalArchivos} archivos empaquetados`
                      : "Calculando el contenido del proyecto…"}
                    {zipIniciadoEn ? ` · iniciado hace ${elapsedLabel(zipIniciadoEn)}` : ""}
                  </div>
                  <Tooltip title="Detiene la generación en curso; podrás volver a generar el paquete cuando quieras">
                    <Button
                      size="small"
                      danger
                      type="link"
                      loading={cancelandoZip}
                      onClick={handleCancelarZip}
                      style={{ padding: 0, marginTop: 4 }}
                    >
                      Cancelar generación
                    </Button>
                  </Tooltip>
                </div>
              )}
              {permissionExport && (zipEstado === "desactualizado" || zipEstado === null) && (
                <div style={{ textAlign: "center" }}>
                  <Tooltip title={uploading ? "Espera a que termine la subida en curso antes de generar el paquete" : "Empaqueta en un ZIP todo el contenido actual del proyecto para poder descargarlo"}>
                    <Button
                      type="primary"
                      icon={<SyncOutlined />}
                      loading={generandoZip}
                      disabled={uploading}
                      onClick={handleGenerarZip}
                      style={{
                        background: NAVY,
                        borderRadius: 10,
                        height: 44,
                        fontWeight: 450,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                        whiteSpace: "normal",
                        width: "90%",
                        maxWidth: 220
                      }}
                    >
                      Generar paquete de descarga
                    </Button>
                  </Tooltip>
                  <div style={{ marginTop: 6, fontSize: 11, color: "#8c9bab" }}>
                    {uploading ? "Esperando a que termine la subida…" : "Hay cambios sin empaquetar para descarga"}
                  </div>
                </div>
              )}
            </Col>
          </Row>
        </div>

        <div
          style={{
            background: "#f7f9fc",
            borderRadius: 12,
            padding: "14px 16px",
            marginBottom: 16,
            border: "1px solid #eef1f5"
          }}
        >
          <Space wrap size={10}>
            {permissionInsert && (
              <Tooltip title="Sube uno o varios archivos sueltos directamente a esta carpeta">
                <Button
                  icon={<UploadOutlined />}
                  loading={uploading}
                  onClick={handleUploadClick}
                  style={{ borderRadius: 8, color: NAVY, borderColor: "#d7dee7", background: "#fff" }}
                >
                  Subir archivos
                </Button>
              </Tooltip>
            )}
            {permissionInsert && (
              <Tooltip title="Sube una carpeta completa desde tu computador, conservando su estructura de subcarpetas">
                <Button
                  icon={<CloudUploadOutlined />}
                  loading={uploading}
                  onClick={handleFolderUploadClick}
                  style={{ borderRadius: 8, color: NAVY, borderColor: "#d7dee7", background: "#fff" }}
                >
                  Subir carpeta
                </Button>
              </Tooltip>
            )}
            {permissionInsert && (
              <Tooltip title={uploading ? "Espera a que termine la subida en curso" : "Crea una subcarpeta vacía dentro de la carpeta actual"}>
                <Button
                  icon={<FolderAddOutlined />}
                  disabled={uploading}
                  onClick={() => setNuevaCarpetaVisible(true)}
                  style={{ borderRadius: 8, color: NAVY, borderColor: "#d7dee7", background: "#fff" }}
                >
                  Nueva carpeta
                </Button>
              </Tooltip>
            )}
            {rootFolderId && permissionAbrirDrive && (
              <Tooltip title="Abre esta carpeta directamente en Google Drive, fuera de la aplicación">
                <Button
                  icon={<GoogleOutlined />}
                  href={`https://drive.google.com/drive/folders/${rootFolderId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ borderRadius: 8, color: NAVY, borderColor: "#d7dee7", background: "#fff" }}
                >
                  Abrir en Drive
                </Button>
              </Tooltip>
            )}
          </Space>

          {uploading && (
            <div
              style={{
                marginTop: 12,
                padding: "10px 14px",
                background: "#fff",
                border: "1px solid #d7dee7",
                borderRadius: 10
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, gap: 12 }}>
                <Text style={{ fontSize: 12, color: "#5b6b7c", fontWeight: 500 }}>
                  <SyncOutlined spin style={{ marginRight: 6, color: NAVY }} />
                  {uploadProgressText || "Subiendo…"}
                </Text>
                <Space size={10}>
                  <Text style={{ fontSize: 12, color: NAVY, fontWeight: 700, flexShrink: 0 }}>
                    {uploadProgress}%
                  </Text>
                  <Tooltip title="Detiene la subida; lo que ya se alcanzó a subir queda subido, y podrás reintentar el resto">
                    <Button size="small" danger onClick={handleCancelarSubida} style={{ borderRadius: 8 }}>
                      Cancelar
                    </Button>
                  </Tooltip>
                </Space>
              </div>
              <Progress percent={uploadProgress} size="small" showInfo={false} strokeColor={NAVY} />
            </div>
          )}

          {!uploading && failedBatches.length > 0 && (
            <div
              style={{
                marginTop: 12,
                padding: "10px 14px",
                background: "#fff7e6",
                border: "1px solid #ffd591",
                borderRadius: 10,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap"
              }}
            >
              <Text style={{ fontSize: 12, color: "#874d00" }}>
                {uploadCancelled ? "Subida cancelada." : "Algunos archivos no se pudieron subir."}{" "}
                {failedBatches.length} lote(s) pendiente(s).
              </Text>
              <Space size={8}>
                <Tooltip title="Descarta lo pendiente sin volver a intentarlo; lo que ya se subió queda como está">
                  <Button size="small" onClick={handleDescartarPendientes} style={{ borderRadius: 8 }}>
                    Descartar
                  </Button>
                </Tooltip>
                <Tooltip title="Vuelve a intentar solo los lotes que quedaron pendientes, sin repetir lo que ya se subió">
                  <Button
                    size="small"
                    type="primary"
                    icon={<SyncOutlined />}
                    onClick={handleReintentarFallidos}
                    style={{ borderRadius: 8, background: NAVY }}
                  >
                    Reintentar pendientes
                  </Button>
                </Tooltip>
              </Space>
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 12,
            flexWrap: "wrap"
          }}
        >
          <Tooltip title="Busca por nombre dentro de esta carpeta (no busca en subcarpetas)">
            <Input
              allowClear
              placeholder="Buscar por nombre…"
              prefix={<SearchOutlined style={{ color: "#8c9bab" }} />}
              value={filtroNombre}
              onChange={(e) => setFiltroNombre(e.target.value)}
              style={{ width: 240, borderRadius: 8 }}
            />
          </Tooltip>
          <Tooltip title="Filtra la lista por tipo de archivo">
            <Select
              value={filtroTipo}
              onChange={setFiltroTipo}
              options={TIPO_OPTIONS}
              style={{ width: 170 }}
            />
          </Tooltip>
        </div>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={itemsFiltrados}
          loading={loading}
          pagination={{ pageSize: 15 }}
          scroll={{ y: maximized ? "calc(100vh - 420px)" : 380 }}
          size="middle"
        />
      </>
    );
  }

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={maximized ? "100vw" : "90vw"}
      style={maximized ? { top: 0, maxWidth: "100vw", paddingBottom: 0 } : { maxWidth: 1100, top: 24 }}
      styles={{ body: { paddingTop: 8, height: maximized ? "calc(100vh - 110px)" : undefined, overflowY: maximized ? "auto" : undefined } }}
      destroyOnHidden
      title={
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span
              style={{
                background: NAVY,
                color: "#ffffff",
                fontWeight: 700,
                fontSize: 14,
                padding: "4px 12px",
                borderRadius: 8
              }}
            >
              {proyecto}
            </span>
            <div>
              <Title level={5} style={{ margin: 0, color: NAVY }}>
                Informe Técnico Final
              </Title>
              <Text style={{ color: "#8c9bab", fontSize: 13, fontWeight: 400 }}>{nombreProyecto}</Text>
            </div>
          </div>
          <Space style={{ marginRight: 32 }}>
            {folderData?.configurado && (
              <Tooltip title="Ver el registro de subidas, cambios y eliminaciones de este proyecto">
                <Button
                  icon={<HistoryOutlined />}
                  onClick={() => setHistorialVisible(true)}
                  style={{ borderRadius: 8, color: NAVY, borderColor: "#d7dee7" }}
                >
                  Historial
                </Button>
              </Tooltip>
            )}
            <Tooltip title={maximized ? "Salir de pantalla completa" : "Ver en pantalla completa"}>
              <Button
                icon={maximized ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                onClick={() => setMaximized((prev) => !prev)}
                style={{ borderRadius: 8, color: NAVY, borderColor: "#d7dee7" }}
              />
            </Tooltip>
          </Space>
        </div>
      }
    >
      <input
        type="file"
        multiple
        ref={uploadInputRef}
        style={{ display: "none" }}
        onChange={handleFilesSelected}
      />
      <input
        type="file"
        ref={folderUploadInputRef}
        style={{ display: "none" }}
        webkitdirectory=""
        directory=""
        multiple
        onChange={handleFolderSelected}
        key={visible ? "folder-input-active" : "folder-input-hidden"}
      />
      <input
        type="file"
        ref={replaceInputRef}
        style={{ display: "none" }}
        onChange={handleReplaceFileSelected}
      />

      {content}

      <Modal
        open={!!renameTarget}
        title={`Renombrar ${renameTarget?.isFolder ? "carpeta" : "archivo"}`}
        onCancel={closeRename}
        onOk={handleRenameConfirm}
        okText="Guardar"
        cancelText="Cancelar"
        confirmLoading={renaming}
      >
        <Input
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onPressEnter={handleRenameConfirm}
          autoFocus
        />
      </Modal>

      <Modal
        open={nuevaCarpetaVisible}
        title="Nueva carpeta"
        onCancel={() => {
          setNuevaCarpetaVisible(false);
          setNuevaCarpetaNombre("");
        }}
        onOk={handleCrearCarpeta}
        okText="Crear"
        cancelText="Cancelar"
        confirmLoading={creandoCarpeta}
      >
        <Input
          placeholder="Nombre de la carpeta"
          value={nuevaCarpetaNombre}
          onChange={(e) => setNuevaCarpetaNombre(e.target.value)}
          onPressEnter={handleCrearCarpeta}
          autoFocus
        />
      </Modal>

      <HistorialPanel
        open={historialVisible}
        onClose={() => setHistorialVisible(false)}
        codigo={proyecto}
      />
    </Modal>
  );
}
